/* eslint-disable no-shadow */
import { datadogRum } from '@datadog/browser-rum';
import isEqual from 'lodash.isequal';
import get from 'lodash.get';
import values from 'lodash.values';
import pickBy from 'lodash.pickby';
import includes from 'lodash.includes';
import isEmpty from 'lodash.isempty';
import keys from 'lodash.keys';
import Analytics from '@/mixins/Analytics';
import featureFlagSlugs from '../../constants/featureFlagSlugs';
import FeatureFlagsClient from '../../api/FeatureFlagsClient';

const state = {
  flags: {}, // fusebox values and dev panel overrides
  fuseboxFlags: {}, // original values
  featureFlagSlugs,
  expire: Date.now(),
  apiClient: null,
  existingRequest: null,
};

export const mutations = {
  setFeatureFlags(state, payload) {
    const oldFlagsValue = state.fuseboxFlags;
    const oldFlagsExpire = state.expire;
    const newFlagsValue = payload.flags;
    const newFlagExpire = payload.expire;
    state.fuseboxFlags = newFlagsValue; // original values
    state.flags = { ...newFlagsValue };
    state.expire = newFlagExpire;

    Object.entries(newFlagsValue).forEach(([slug, value]) => {
      datadogRum.addFeatureFlagEvaluation(slug, value);
    });

    if (!isEqual(oldFlagsValue, newFlagsValue)) {
      Analytics
        .methods
        .track(
          Analytics
            .data()
            .eventNames
            .featureFlagsUpdated,
          {
            oldFlagsValue,
            oldFlagsExpire,
            newFlagsValue,
            newFlagExpire,
          },
          { withPendo: false, withMixpanel: false },
        );
    }
  },

  setFeatureFlag(state, payload) {
    const oldFlagsValue = { ...state.flags };
    const oldFlagsExpire = state.expire;
    const newFlagsValue = {
      ...state.flags,
      [payload.slug]: payload.value,
    };
    const newFlagExpire = payload?.expire;
    state.flags[payload.slug] = payload.value;

    datadogRum.addFeatureFlagEvaluation(payload.slug, payload.value);
    if (!isEqual(oldFlagsValue, newFlagsValue)) {
      Analytics
        .methods
        .track(
          Analytics
            .data()
            .eventNames
            .featureFlagsUpdated,
          {
            oldFlagsValue,
            oldFlagsExpire,
            newFlagsValue,
            newFlagExpire,
          },
          { withPendo: false, withMixpanel: false },
        );
    }
  },

  setEffectiveFlags(state, flags) {
    // plugin triggers mutation to retain merged effective flags to flags state
    Object.entries(flags).forEach(([slug, value]) => {
      if (state.flags[slug] !== value) {
        datadogRum.addFeatureFlagEvaluation(slug, value);
      }
    });

    state.flags = flags;
  },

  setExpire(state, expire) {
    state.expire = expire;
  },

  setApiClient(state, client) {
    state.apiClient = client;
  },

  setExistingRequest(state, existingRequest) {
    state.existingRequest = existingRequest;
  },

  clearExistingRequest(state) {
    state.existingRequest = null;
  },
};

export const actions = {
  async getApiClient({
    state, commit, dispatch,
  }) {
    let { jwtToken, refreshToken } = await dispatch('getJwtTokens');

    if (!jwtToken) {
      await dispatch('refreshJwtTokens');
      ({ jwtToken, refreshToken } = await dispatch('getJwtTokens'));
    }

    const client = new FeatureFlagsClient({
      jwtToken,
      refreshToken,
    });

    commit('setApiClient', client);

    return state.apiClient;
  },

  async getFlag({ dispatch, getters, state }, flagSlug) {
    if (!getters.flagsExistInStore([flagSlug])) {
      await dispatch('fetchFeatureFlagsFromAPI');
    }

    if (!getters.isAuthorized) {
      return false;
    }

    return get(state.flags, flagSlug, false);
  },

  async getFlagGroup({ state, dispatch, getters }, flagSlugs) {
    if (!getters.flagsExistInStore(values(flagSlugs))) {
      await dispatch('fetchFeatureFlagsFromAPI');
    }

    const flags = pickBy(state.flags, (value, key) => includes(flagSlugs, key));
    return flags;
  },

  async getAllFeatureFlags({ state, dispatch, getters }) {
    if (!getters.flagsExistInStore(values(state.featureFlagSlugs))) {
      await dispatch('fetchFeatureFlagsFromAPI');
    }

    return state.flags;
  },

  fetchFeatureFlagsFromAPI({
    commit,
    getters,
    dispatch,
    state,
    rootState,
  }) {
    if (!state.existingRequest) {
      const defaults = values(state.featureFlagSlugs)
        .reduce((aggregator, currentValue) => ({
          ...aggregator,
          [currentValue]: false,
        }), {});

      const existingRequest = (async () => {
        try {
          const client = await dispatch('getApiClient');
          const response = await client.getAllFlags();

          commit('setFeatureFlags', {
            flags: {
              ...defaults,
              ...response.data.data.flags,
            },
            expire: Date.now() + getters.defaultExpirationTTL,
          });
          return state.featureFlags;
        } catch (e) {
          const receivedError = e?.message || e;
          Analytics.methods.noticeError(
            new Error(
              'Feature Flags setting failed from feature-flags-javascript error or from request',
            ),
            {
              userId: rootState.authorization?.user?.id,
              apiError: receivedError,
            },
          );
          commit('setFeatureFlags', {
            flags: {},
            expire: Date.now() + getters.defaultExpirationTTL,
          });
          return e;
        } finally {
          commit('clearExistingRequest');
        }
      })();

      commit('setExistingRequest', existingRequest);
      return existingRequest;
    }
    return state.existingRequest;
  },
  async fetchFeatureFlagFromAPI({
    dispatch,
  }, flagSlug) {
    try {
      const client = await dispatch('getApiClient');
      const response = await client.get(flagSlug);
      return get(response, 'data.data.value', false) || false;
    } catch (error) {
      throw new Error(error.message);
    }
  },
  removeFeatureFlags({ commit, dispatch }) {
    dispatch('featureFlagOverrides/clearOverrides', null, { root: true });
    commit('setFeatureFlags', {
      flags: {},
      expire: Date.now(),
    });
  },
  updateFeatureFlags({ commit }, payload) {
    commit('setFeatureFlags', payload);
  },
  updateFeatureFlag({ commit, dispatch }, payload) {
    commit('setFeatureFlag', payload);
    dispatch('featureFlagOverrides/toggleOverride', payload, { root: true });
  },
  clearFeatureFlagClient({ commit }) {
    commit('setApiClient', null);
  },
};

export const getters = {
  flagsExistInStore: (state) => (slugs) => {
    const storeIsNotEmpty = !isEmpty(state.flags);
    const storeIsNotExpired = state.expire > Date.now();
    let storeContainsWantedSlugs = true;

    slugs.forEach((slug) => {
      if (!includes(keys(state.flags), slug)) {
        storeContainsWantedSlugs = false;
      }
    });

    return storeIsNotEmpty && storeIsNotExpired && storeContainsWantedSlugs;
  },
  slugIsDefined: (state) => (slug) => {
    const slugIsDefined = values(state.featureFlagSlugs)
      .includes(slug);

    if (!slugIsDefined && import.meta.env.DEV) {
      window.console.warn(`Someone tried to check for the unknown feature flag slug: ${slug}!`);
    }
    return slugIsDefined;
  },
  defaultExpirationTTL: () => Number.parseInt(import.meta.env.VITE_FEATURE_FLAG_TTL, 10)
    || (1000 * 60 * 20),
  expirationDateTime: (state) => new Date(state.expire),
};

const featureFlags = {
  state,
  mutations,
  actions,
  getters,
};

export default featureFlags;
